import { useRef, useState } from 'react';
import { FormApi, FormState } from 'informed';
import isEmail from 'validator/lib/isEmail';
import jschardet from 'jschardet';
import Uppy from '@uppy/core';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getAttributesWithMergedOptions } from 'shared-helpers/customAttributes';
import { useApiData } from 'lib/api';
import { csvStringToArray } from 'lib/common';
import { getCustomAttributesTypes } from 'lib/customAttributes';
import {
  Button,
  LoadingIndicator,
  ModalButtonWithForm
} from 'components/common';
import { Dropdown } from 'components/common/dropdown';
import { Checkbox, FormGroup, RadioGroup } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';
import { UsersImportCsvPreview, UsersImportCsvRegistration } from '.';

import type { EmailTemplateData, User } from 'shared-types';
import type { CreateUsersReq } from 'shared-types/requests';
import type { UpdateUserObject } from 'components/users/Users';

export type FormValuesInUsersImportCsv = {
  accountType: 'email' | 'username';
  delimiter: ',' | ',' | '  ' | ' ';
  email: EmailTemplateData['email'] & { selectedTemplateIdentifier: string };
  ignoreFirstLine: boolean;
  handleExistingUsers: 'ignore' | 'update' | 'update-ignore-empty';
  password?: string;
  passwordExpirationInDays?: number;
  registrationUsePassword: boolean;
};

export type UsersDataInUsersImportCsv = (User & {
  error: string;
  status: 'duplicate' | 'ignore' | 'invalid' | 'ok' | 'update';
})[];

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: (values: UpdateUserObject) => Promise<void>;
}

const UsersImportCsv = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    serverConfig: { userCustomAttributes },
    theme,
    user
  } = useReduxState();

  const [fileError, setFileError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [usersData, setUsersData] = useState<UsersDataInUsersImportCsv>(null);
  const [view, setView] = useState<'initial' | 'preview' | 'registration'>(
    'initial'
  );

  const formApi = useRef<FormApi<FormValuesInUsersImportCsv>>(undefined);
  const fileUploader = useRef<any>(undefined);

  const { data: users, loading } = useApiData({
    url: '/admin/users-admin-emails',
    onlyIf: isVisible
  });

  const viewableUserCustomAttributes = getAttributesWithMergedOptions({
    language,
    onlyViewable: true,
    requestUser: user,
    userCustomAttributes
  });

  const handleAccountTypeChange = (
    value: FormValuesInUsersImportCsv['accountType']
  ) => {
    formApi.current?.setValue('registrationUsePassword', value === 'username');
  };

  const handleHide = () => {
    setFileError(false);
    setIsVisible(false);
    setUsersData(null);
    setView('initial');
  };

  const handleFileAdded = () => {
    setFileError(null);
  };

  const handleGoBack = () => {
    if (view === 'registration') setView('preview');
    else {
      setUsersData(null);
      setView('initial');
    }
  };

  const handlePreview = async (file, values: FormValuesInUsersImportCsv) => {
    const { accountType, handleExistingUsers, ignoreFirstLine } = values;

    // first read file as binary to get encoding
    let encoding = (await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileContents = event.target.result as string;
        const encoding = jschardet.detect(fileContents.toString()).encoding;
        resolve(encoding);
      };

      reader.readAsBinaryString(file.data);
    })) as string;

    // for some reason jschardet may detect the encoding incorrectly
    // ex. we probably do not need to match windows-1255 for hebrew,
    // so in this case we will just convert it to windows-1252 instead
    if (encoding === 'windows-1255') encoding = 'windows-1252';

    // then read the file with the given encoding
    const csvData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileContents = event.target.result as string;
        resolve(fileContents);
      };

      reader.readAsText(file.data, encoding || 'UTF-8');
    });

    const arrayData = csvStringToArray(csvData as string, values.delimiter);

    // existing user data
    const existingUsers =
      accountType === 'email'
        ? users.emails
        : users.users.map((user) => user.username);

    // parse and validata data
    const usersDataFromCsv = [];
    const alreadyAddedKeys = {};
    const accountIsEmail = accountType === 'email';
    arrayData.forEach((values, valuesIndex) => {
      // ignore first line if configured
      if (valuesIndex === 0 && ignoreFirstLine) return;

      // ignore empty lines
      if (values.length === 1 && values[0] === '') return;

      let error = '';
      let status = 'ok';

      // read values
      const email = accountIsEmail ? values[0]?.trim() : null;
      const valuesWithoutEmail = accountIsEmail ? values.slice(1) : values;
      const [untrimmedUsername, firstName, lastName, ...customAttributeValues] =
        valuesWithoutEmail;
      const username = untrimmedUsername?.trim();

      // if user exists, identify it at the status column
      const isAlreadyUser = existingUsers.includes(
        accountIsEmail ? email : username
      );
      if (
        (handleExistingUsers === 'update' ||
          handleExistingUsers === 'update-ignore-empty') &&
        isAlreadyUser
      ) {
        status = 'update';
      } else if (handleExistingUsers === 'ignore' && isAlreadyUser) {
        status = 'ignore';
      }

      // ignore lines that are missing an email or username value
      if (!values[0]) {
        error = accountIsEmail
          ? messages.usersImportCsvMissingEmail
          : messages.usersImportCsvMissingUsername;
        status = 'invalid';
      }

      // ignore lines where an email is used as username
      if (isEmail(username)) {
        error = messages.authInvalidEmailAsUsername;
        status = 'invalid';
      }

      // ignore lines that are missing a valid email
      if (accountIsEmail && !isEmail(email)) {
        error = messages.usersInvalidEmail;
        status = 'invalid';
      }

      // mark duplicates
      if (status !== 'invalid') {
        const identifier = accountIsEmail ? email : username;
        if (alreadyAddedKeys[identifier]) status = 'duplicate';
        else alreadyAddedKeys[identifier] = true;
      }

      // create custom attributes
      const customAttributes = {};
      viewableUserCustomAttributes.forEach((attribute, attributeIndex) => {
        if (
          handleExistingUsers === 'update-ignore-empty' &&
          customAttributeValues[attributeIndex] === ''
        ) {
          return;
        }

        if (attribute.type === 'Number') {
          if (Number(customAttributeValues[attributeIndex])) {
            customAttributes[attribute.key] = Number(
              customAttributeValues[attributeIndex]
            );
          }
        } else if (attribute.type === 'Boolean') {
          customAttributes[attribute.key] = Boolean(
            customAttributeValues[attributeIndex] === 'true'
          );
        } else if (
          attribute.type === 'Array' ||
          attribute.type === 'ArrayWithOptions'
        ) {
          customAttributes[attribute.key] = customAttributeValues[
            attributeIndex
          ]
            ?.split(',')
            .map((value) => value?.trim())
            .filter((value) => Boolean(value));
        } else {
          customAttributes[attribute.key] =
            customAttributeValues[attributeIndex];
        }
      });

      // add user to data
      usersDataFromCsv.push({
        error,
        status,
        email,
        username,
        firstName,
        lastName,
        customAttributes
      });
    });

    setUsersData(usersDataFromCsv);
  };

  const handleSubmit = async (values: FormValuesInUsersImportCsv) => {
    if (view === 'initial') {
      const file = fileUploader.current.state.file;
      if (!file) {
        setFileError(messages.commonFieldRequired);
        return false;
      }

      setFileError(null);

      await handlePreview(file, values);
      setView('preview');
      return false;
    }

    if (view === 'preview') {
      setView('registration');
      return false;
    }

    const response = await api.post('/admin/users-new', {
      emailContent: values.email as CreateUsersReq['emailContent'],
      handleExistingUsers: values.handleExistingUsers,
      language,
      password: values.registrationUsePassword
        ? values.password
        : values.registrationUsePassword === undefined
        ? undefined
        : '',
      passwordExpirationInDays: values.passwordExpirationInDays,
      users: usersData.filter(
        (user) => user.status === 'ok' || user.status === 'update'
      )
    } satisfies CreateUsersReq);

    await updateUsers({ showLoading: false });

    if (response.data.accountsCreated > 0) {
      dispatch.addNotice({
        text: `${response.data.accountsCreated} ${messages.settingsAddUsersAccountsCreated}`,
        type: 'success'
      });
    }

    if (response.data.accountsUpdated > 0) {
      dispatch.addNotice({
        text: getMessageWithPlaceholders({
          message: messages.settingsAddUsersAccountsUpdated,
          placeholders: { numberOfAccounts: response.data.accountsUpdated }
        }),
        type: 'success'
      });
    }

    if (
      response.data.errors.email_taken.length ||
      response.data.errors.username_taken.length
    ) {
      dispatch.addNotice({
        text: `${
          values.accountType === 'email'
            ? messages.authEmailInUseError
            : messages.authUsernameInUseError
        }:<br /> ${
          values.accountType === 'email'
            ? response.data.errors.email_taken.join(',<br />')
            : response.data.errors.username_taken.join(',<br />')
        }`,
        type: 'warning'
      });
    }

    if (response.data.errors.email_as_username.length) {
      dispatch.addNotice({
        text: `${
          messages.authInvalidUsername
        }:<br /> ${response.data.errors.email_as_username.join(',<br />')}`,
        type: 'warning'
      });
    }
  };

  const setFileUploader = (uploader: Uppy.Uppy) => {
    fileUploader.current = uploader;
  };

  const userAttributesTypes = getCustomAttributesTypes({ messages });

  const initialValues: FormValuesInUsersImportCsv = {
    accountType: 'email',
    delimiter: ',',
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    ignoreFirstLine: false,
    handleExistingUsers: 'ignore',
    registrationUsePassword: false
  };

  return (
    <div>
      <Dropdown
        buttonStyle={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        category="success"
        icon="chevron-down"
        size="header"
        styleContentButtons={false}
      >
        <ModalButtonWithForm
          buttonCategory="success"
          buttonIcon="file"
          buttonIsDropdown={true}
          buttonText={messages.usersImportCsv}
          contentStyle={{ padding: 0 }}
          footerContent={
            view !== 'initial' ? (
              <Button
                category="light"
                icon="chevron-left"
                onClick={handleGoBack}
                style={{ float: 'left' }}
                text={messages.commonGoBack}
              />
            ) : null
          }
          initialValues={initialValues}
          modalActionText={
            {
              initial: messages.usersImportContinueToPreview,
              preview: messages.usersImportContinueToRegistration,
              registration: messages.commonConfirm
            }[view]
          }
          modalTitle={messages.usersImportCsv}
          modalWidth="wide"
          onHide={handleHide}
          onShow={() => setIsVisible(true)}
          onSubmit={handleSubmit}
          setFormApi={(form) => (formApi.current = form)}
        >
          {(formState: FormState<FormValuesInUsersImportCsv>) =>
            loading ? (
              <LoadingIndicator />
            ) : (
              <div>
                <div
                  className="import-form"
                  style={{ display: view === 'initial' ? 'block' : 'none' }}
                >
                  <div className="format">
                    <FormGroup
                      label={messages.usersImportExampleFormat}
                      maxWidth="100%"
                    >
                      <div className="format-content">
                        <table className="format-table">
                          <thead>
                            <tr className="format-header">
                              {formState.values?.accountType === 'email' ? (
                                <th>{messages.commonEmail}</th>
                              ) : null}
                              <th>{messages.commonUsername}</th>
                              <th>{messages.usersFirstName}</th>
                              <th>{messages.usersLastName}</th>
                              {viewableUserCustomAttributes?.map(
                                (attribute) => (
                                  <th key={attribute.key}>
                                    {attribute.titleTranslations?.[language] ||
                                      attribute.title}
                                  </th>
                                )
                              )}
                            </tr>
                          </thead>

                          <tbody>
                            <tr className="format-row">
                              {formState.values?.accountType === 'email' ? (
                                <td>{messages.loginEmailPlaceholder}</td>
                              ) : null}
                              <td>
                                {messages.loginEmailPlaceholder?.split('@')[0]}
                              </td>
                              <td style={{ textTransform: 'capitalize' }}>
                                {
                                  messages.loginEmailPlaceholder
                                    ?.split('@')[0]
                                    .split('.')[0]
                                }
                              </td>
                              <td style={{ textTransform: 'capitalize' }}>
                                {
                                  messages.loginEmailPlaceholder
                                    ?.split('@')[0]
                                    .split('.')[1]
                                }
                              </td>
                              {viewableUserCustomAttributes?.map(
                                (attribute) => (
                                  <td key={attribute.key}>
                                    {
                                      {
                                        String: userAttributesTypes['String'],
                                        StringWithOptions:
                                          userAttributesTypes['String'],
                                        Text: userAttributesTypes['String'],
                                        Number: userAttributesTypes['Number'],
                                        Boolean: 'true',
                                        Array: `${userAttributesTypes['String']}1, ${userAttributesTypes['String']}2`,
                                        ArrayWithOptions: `${userAttributesTypes['String']}1, ${userAttributesTypes['String']}2`
                                      }[attribute.type]
                                    }
                                  </td>
                                )
                              )}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </FormGroup>
                  </div>

                  <div className="row-md">
                    <div className="col-1of2">
                      <FormGroup label={messages.usersImportDelimiter}>
                        <RadioGroup
                          id="users-import-csv-delimiter"
                          field="delimiter"
                          options={[
                            {
                              id: 'comma',
                              label: messages.usersImportDelimiterComma,
                              value: ','
                            },
                            {
                              id: 'semicolon',
                              label: messages.usersImportDelimiterSemicolon,
                              value: ';'
                            },
                            {
                              id: 'tab',
                              label: messages.usersImportDelimiterTab,
                              value: '	'
                            },
                            {
                              id: 'space',
                              label: messages.usersImportDelimiterSpace,
                              value: ' '
                            }
                          ]}
                          style={{ width: 150 }}
                        />
                      </FormGroup>

                      <FormGroup label={messages.usersImportFirstLine}>
                        <Checkbox
                          field="ignoreFirstLine"
                          id="users-import-csv-ignore-first-line"
                          label={messages.usersImportFirstLineInfo}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-1of2">
                      <FormGroup label={messages.createAccounts}>
                        <RadioGroup
                          id="users-import-csv-import-type"
                          inline={true}
                          field="accountType"
                          onChange={handleAccountTypeChange}
                          options={[
                            {
                              id: 'email',
                              label: messages.userCreateWithEmail,
                              value: 'email'
                            },
                            {
                              id: 'username',
                              label: messages.userCreateWithoutEmail,
                              value: 'username'
                            }
                          ]}
                        />
                      </FormGroup>

                      <FormGroup label={messages.usersExists}>
                        <RadioGroup
                          id="users-import-csv-existing-users"
                          field="handleExistingUsers"
                          options={[
                            {
                              id: 'ignore',
                              label: messages.commonIgnore.toLowerCase(),
                              value: 'ignore'
                            },
                            {
                              id: 'update-ignore-empty',
                              label:
                                messages.usersImportCsvUpdateIgnoreEmpty?.toLowerCase(),
                              value: 'update-ignore-empty'
                            },
                            {
                              id: 'update',
                              label:
                                messages.usersImportCsvUpdateAll?.toLowerCase(),
                              value: 'update'
                            }
                          ]}
                        />
                      </FormGroup>

                      <FormGroup label={messages.usersImportCsvFile}>
                        <div className="uploader">
                          <FileUploader
                            allowedFileTypes={['.csv']}
                            dragDrop={true}
                            dragDropActions={false}
                            dragDropCompactStyle={true}
                            getUploader={setFileUploader}
                            icon="file"
                            id="users-import-csv-file"
                            onFileAdded={handleFileAdded}
                            placeholder={messages.uploaderChooseFile}
                            uuidAsFilename={false}
                          />
                        </div>

                        {fileError ? (
                          <div className="file-error">{fileError}</div>
                        ) : null}
                      </FormGroup>
                    </div>
                  </div>
                </div>

                <div
                  className="import-preview"
                  style={{ display: view === 'preview' ? 'block' : 'none' }}
                >
                  <UsersImportCsvPreview
                    accountType={formState.values?.accountType}
                    userCustomAttributes={viewableUserCustomAttributes}
                    usersData={usersData}
                  />
                </div>

                <div
                  className="import-registration"
                  style={{
                    display: view === 'registration' ? 'block' : 'none'
                  }}
                >
                  <UsersImportCsvRegistration
                    emailTemplates={emailTemplates}
                    formApi={formApi.current}
                    formState={formState}
                    isVisible={view === 'registration'}
                    usersData={usersData}
                  />
                </div>
              </div>
            )
          }
        </ModalButtonWithForm>
      </Dropdown>

      <style jsx>{`
        .import-form,
        .import-registration {
          padding: 15px;
        }
        .format {
          background-color: ${theme.lightGreyLighter};
          border-radius: 7px;
          margin-bottom: 25px;
          padding: 15px;
        }
        .format-content {
          overflow-x: auto;
          width: 100%;
        }
        .format-table {
          background-color: white;
          border-collapse: collapse;
        }
        .format-table th,
        .format-table td {
          border: 2px solid ${theme.lightGreyDarkActive};
          font-size: 1.4rem;
          font-weight: 600;
          padding: 5px 10px;
        }
        .format-table th {
          color: ${theme.textColorLight};
          font-weight: 700;
          white-space: nowrap;
        }
        .format-table td {
          color: ${theme.textColorLighter};
          font-weight: 600;
          white-space: nowrap;
        }
        .uploader {
          display: block;
        }
        .file-error {
          font-size: 1.3rem;
          color: ${theme.dangerColor};
          padding: 5px 3px 3px;
        }
      `}</style>
    </div>
  );
};

export default UsersImportCsv;
